/** Provider-committed CI dates are calendar-day operational targets. */
import { DEFAULT_TZ, dateOnlyInTz } from '../timezone';
import type { ComplianceDeadlineItem } from './types';

const PROVISION = 'Statura operational target (Strengthened Standard 2 continuous improvement)';
const DUE_SOON_DAYS = 7;

/** W1 lifecycle semantics: date-only keys stay dates; legacy instants use the tenant zone. */
export function ciDueDateKey(value: string | null | undefined, timezone: string): string | null {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const parsed = new Date(`${value}T12:00:00Z`);
    return Number.isFinite(parsed.getTime()) && dateOnlyInTz(parsed, 'UTC') === value
      ? value
      : null;
  }
  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime()) ? dateOnlyInTz(parsed, timezone) : null;
}

function deadlineDate(value: string, now: Date, timezone: string) {
  const key = ciDueDateKey(value, timezone);
  if (!key) return null;
  const today = dateOnlyInTz(now, timezone);
  // UTC is used only for arithmetic on validated calendar keys, never to derive a business date.
  const daysUntil =
    (Date.parse(`${key}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000;
  const status = daysUntil < 0 ? 'OVERDUE' : daysUntil <= DUE_SOON_DAYS ? 'DUE_SOON' : 'UPCOMING';
  return { dueAt: key, status, meta: { daysUntil } } as const;
}

export interface CiItemRow {
  id: string;
  referenceNumber: string;
  targetCompletionDate: string;
  ownerUserId?: string | null;
  facilityId?: string | null;
}

export function evaluateCiItemDeadline(
  row: CiItemRow,
  now: Date,
  timezone: string = DEFAULT_TZ,
): { item: ComplianceDeadlineItem } | null {
  const date = deadlineDate(row.targetCompletionDate, now, timezone);
  if (!date) return null;
  return {
    item: {
      id: `ci-item:${row.id}`,
      register: 'CONTINUOUS_IMPROVEMENT',
      recordRef: row.referenceNumber,
      title: 'Continuous improvement target date',
      kind: 'TARGET',
      provisionOrTargetLabel: PROVISION,
      href: `/compliance/continuous-improvement/${row.id}`,
      ownerUserId: row.ownerUserId ?? null,
      facilityId: row.facilityId ?? null,
      ...date,
    },
  };
}

/** Cron compatibility entry point: only overdue from tenant midnight on D+1. */
export function evaluateCiItemOverdue(row: CiItemRow, now: Date, timezone: string = DEFAULT_TZ) {
  const result = evaluateCiItemDeadline(row, now, timezone);
  return result?.item.status === 'OVERDUE' ? result : null;
}

export interface CiActionRow {
  id: string;
  improvementId: string;
  dueDate: string;
  ownerUserId?: string | null;
  facilityId?: string | null;
}

export function evaluateCiActionDeadline(
  row: CiActionRow,
  now: Date,
  timezone: string = DEFAULT_TZ,
): { item: ComplianceDeadlineItem } | null {
  const date = deadlineDate(row.dueDate, now, timezone);
  if (!date) return null;
  return {
    item: {
      id: `ci-action:${row.id}`,
      register: 'CONTINUOUS_IMPROVEMENT',
      recordRef: row.id,
      title: 'Continuous improvement action due',
      kind: 'TARGET',
      provisionOrTargetLabel: PROVISION,
      href: `/compliance/continuous-improvement/${row.improvementId}`,
      ownerUserId: row.ownerUserId ?? null,
      facilityId: row.facilityId ?? null,
      ...date,
    },
  };
}

export function evaluateCiActionOverdue(
  row: CiActionRow,
  now: Date,
  timezone: string = DEFAULT_TZ,
) {
  const result = evaluateCiActionDeadline(row, now, timezone);
  return result?.item.status === 'OVERDUE' ? result : null;
}
